import type { LocationSearchParams, LocationSearchResponse } from '@/api/routes/location/index'
import type { UserLocation } from '@/api/schema'
import { Row } from '@/app/components/Flex/Flex'
import { Input } from '@/app/components/Input/Input'
import { Text } from '@/app/components/Text/Text'
import { Lib } from '@/app/components/UI/Lib'
import { UI } from '@/app/components/UI/UI'
import { api } from '@/app/utils/api'
import debounce from 'lodash.debounce'
import type { ComponentChild, FunctionalComponent } from 'preact'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'

export const LocationFormPage: FunctionalComponent<{
  placeholder?: string
  firstMessage?: ComponentChild
  onSelect?: (location: UserLocation) => void | Promise<void>
}> = props => {
  const [query, setQuery] = useState('')
  const [locations, setLocations] = useState<LocationSearchResponse | null>(null)
  const [searchLoading, setSearchLoading] = useState(0)
  const [loading, setLoading] = useState(false)

  let controller = useRef<AbortController | null>(null)

  const search = useCallback(
    debounce(async () => {
      controller.current?.abort()

      if (query.length > 2) {
        try {
          setSearchLoading(p => p + 1)

          controller.current = new AbortController()

          const cleaned = query.replace(/[^\p{L}]+/gu, ' ')
          const resp = await api.get<LocationSearchResponse>('/location', {
            params: {
              q: cleaned,
            } satisfies LocationSearchParams,
            signal: controller.current.signal,
          })
          setLocations(resp.data)
        }
        catch (e) {
          console.error(e)
        }
        finally {
          setSearchLoading(p => p - 1)
        }
      }
      else {
        setLocations(null)
      }
    }, 300),
    [query],
  )

  useEffect(() => {
    search()
  }, [query])

  return (
    <Lib.Fields>
      <Input
        autoFocus
        placeholder={props.placeholder ?? 'Место рождения'}
        onInput={setQuery}
        onChange={setQuery}
        value={query}
        showSpinner={searchLoading > 0}
        loading={loading}
      />

      {locations === null
        ? (
          <Lib.SystemMessage>
            {props.firstMessage ?? (
              <>
                Начни вводить место рождения и выбери подходящий вариант из списка.
                <br />
                <br />
                Если его нет, укажи ближайший крупный город.
              </>
            )}
          </Lib.SystemMessage>
        )
        : (
          locations.length > 0
            ? (
              locations.slice(0, 5).map(location => (
                <UI.Panel
                  hover
                  key={location.id}
                  onClick={async () => {
                    try {
                      setLoading(true)
                      await props.onSelect?.(location)
                    }
                    catch (e) {
                      // TODO: Show error
                      console.error(e)
                    }
                    finally {
                      setLoading(false)
                    }
                  }}
                >
                  <Row gap={5}>
                    <Text
                      fontSize={16}
                      fontWeight={600}
                    >
                      {location.name_ru || location.name_en}
                    </Text>
                    <Text
                      opacity={0.5}
                      fontSize={14}
                      fontSizeMobile={13}
                    >
                      {location.region_ru || location.region_en}
                    </Text>
                  </Row>
                </UI.Panel>
              ))
            )
            : (
              <Lib.SystemMessage>
                Место не найдено.
                <br />
                <br />
                Попробуй указать ближайший крупный населённый пункт.
              </Lib.SystemMessage>
            )
        )}
    </Lib.Fields>
  )
}
